import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);

const categoryNames = {
  'tortas-especiales': 'Tortas Especiales',
  'tortas-personalizadas': 'Tortas Personalizadas',
  'tortas-tematicas': 'Tortas Temáticas',
  'mesa-dulces': 'Mesa de Dulces',
  'detalles-regalos': 'Detalles y Regalos',
  'tortas': 'Tortas',
  'refrigeradas': 'Refrigeradas',
  'refrigeradas-premium': 'Refrigeradas Premium',
  'clasicas': 'Clasicas',
  'saludables': 'Saludables',
  'grados': 'Grados',
  'comunion': 'Primera Comunión',
  'pide-para-hoy': 'Pide para Hoy',
  'pinateria': 'Piñatería',
  'panaderia': 'Panadería',
  'bebidas': 'Bebidas',
  'cafeteria': 'Cafetería'
};

const emptyProduct = { name: '', description: '', category: 'clasicas', base_price: 0, image: '', images: [], available: true, preparation_days: 2, is_featured: false, is_popular: false, pide_hoy: false, delivery_methods: ['pickup', 'delivery'], allow_color: true, allow_flavor: true, allow_filling: true, flavors: [], fillings: [], show_on_website: true, include_in_cash_closing: true };

const compressImage = (file, maxWidth = 600) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          'image/jpeg',
          0.8
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};


export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`, { withCredentials: true, headers: getHeaders() });
      setProducts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const [globalFlavors, setGlobalFlavors] = useState([]);
  const [globalFillings, setGlobalFillings] = useState([]);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { withCredentials: true, headers: getHeaders() });
      setGlobalFlavors(data.flavors || []);
      setGlobalFillings(data.fillings || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { 
    fetchProducts();
    fetchSettings();
  }, []);

  const openCreate = () => { setEditing(null); setForm({ ...emptyProduct, sizes: [], flavors: [], fillings: [], delivery_methods: ['pickup', 'delivery'] }); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, description: p.description, category: p.category, base_price: p.base_price, image: p.image, images: p.images || [], available: p.available, preparation_days: p.preparation_days, is_featured: p.is_featured, is_popular: p.is_popular, pide_hoy: p.pide_hoy || false, show_on_website: p.show_on_website !== false, sizes: p.sizes || [], flavors: p.flavors || [], fillings: p.fillings || [], delivery_methods: p.delivery_methods || ['pickup', 'delivery'], allow_color: p.allow_color !== false, allow_flavor: p.allow_flavor !== false, allow_filling: p.allow_filling !== false }); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/admin/products/${editing.id}`, form, { withCredentials: true, headers: getHeaders() });
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/admin/products`, { ...form, complements: [] }, { withCredentials: true, headers: getHeaders() });
        toast.success('Producto creado');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch {
      toast.error('Error al guardar producto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este producto?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Producto eliminado');
      fetchProducts();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleFileUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const toastId = toast.loading('Subiendo imagen...');
    try {
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        try {
          fileToUpload = await compressImage(file, 600);
        } catch (compressErr) {
          console.error('Error compressing image, uploading original:', compressErr);
        }
      }

      const formData = new FormData();
      formData.append('file', fileToUpload);

      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers
      });

      setForm(prev => {
        const newImages = [...(prev.images || [])];
        while (newImages.length <= index) {
          newImages.push('');
        }
        newImages[index] = data.url;
        const compacted = newImages.filter(img => img && img.trim() !== '');
        const mainImage = compacted.length > 0 ? compacted[0] : '';
        return { ...prev, images: compacted, image: mainImage };
      });

      toast.success('Imagen subida', { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error('Error al subir imagen', { id: toastId });
    }
  };

  const handleRemoveImage = (index) => {
    setForm(prev => {
      const newImages = [...(prev.images || [])];
      newImages.splice(index, 1);
      const compacted = newImages.filter(img => img && img.trim() !== '');
      const mainImage = compacted.length > 0 ? compacted[0] : '';
      return { ...prev, images: compacted, image: mainImage };
    });
  };

  const exportProductsPDF = () => {
    if (!products || products.length === 0) {
      toast.error('No hay productos para exportar');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Por favor permite las ventanas emergentes para generar el PDF');
      return;
    }

    const categoryTotals = {};
    let activeCount = 0;
    let inactiveCount = 0;

    products.forEach(p => {
      const catLabel = categoryNames[p.category] || p.category || 'Sin Categoría';
      categoryTotals[catLabel] = (categoryTotals[catLabel] || 0) + 1;
      if (p.available !== false) activeCount++;
      else inactiveCount++;
    });

    const rowsHtml = products.map((p, idx) => {
      const catLabel = categoryNames[p.category] || p.category || 'Sin Categoría';
      const sizesHtml = (p.sizes && p.sizes.length > 0)
        ? p.sizes.map(s => `<div>• <strong>${s.name}:</strong> ${formatPrice(s.price)} (${s.prep_days ?? p.preparation_days ?? 2}d)</div>`).join('')
        : `<span style="color: #666;">Precio base (${p.preparation_days ?? 2}d prep.)</span>`;

      const flavorsText = (p.flavors && p.flavors.length > 0)
        ? p.flavors.map(f => f.name).join(', ')
        : (p.allow_flavor !== false ? 'Todos / Estándar' : 'N/A');

      const fillingsText = (p.fillings && p.fillings.length > 0)
        ? p.fillings.map(f => f.name).join(', ')
        : (p.allow_filling !== false ? 'Todos / Estándar' : 'N/A');

      const statusText = p.available !== false ? 'Activo' : 'Inactivo';
      const statusColor = p.available !== false ? '#166534' : '#991b1b';
      const statusBg = p.available !== false ? '#dcfce7' : '#fee2e2';

      return `
        <tr>
          <td style="text-align: center; font-weight: bold; color: #75664d;">${idx + 1}</td>
          <td>
            <div style="font-weight: bold; color: #46371c; font-size: 12px;">${p.name || ''}</div>
            ${p.description ? `<div style="color: #666; font-size: 10px; margin-top: 2px;">${p.description}</div>` : ''}
          </td>
          <td>
            <span style="background-color: #f4f1ea; color: #46371c; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold;">
              ${catLabel}
            </span>
          </td>
          <td style="font-weight: bold; color: #46371c; white-space: nowrap;">${formatPrice(p.base_price || 0)}</td>
          <td style="font-size: 10px; line-height: 1.35;">${sizesHtml}</td>
          <td style="font-size: 10px; color: #555;">
            <div><strong>Sabores:</strong> ${flavorsText}</div>
            <div style="margin-top: 2px;"><strong>Rellenos:</strong> ${fillingsText}</div>
          </td>
          <td style="text-align: center;">
            <span style="background-color: ${statusBg}; color: ${statusColor}; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold;">
              ${statusText}
            </span>
            <div style="font-size: 9px; color: #666; margin-top: 3px;">
              ${p.show_on_website !== false ? '🌐 Web' : '🔒 Oculto Web'}
              ${p.include_in_cash_closing !== false ? ' • 💰 Caja' : ''}
            </div>
          </td>
        </tr>
      `;
    }).join('');

    const summaryRowsHtml = Object.entries(categoryTotals).map(([cat, count]) => `
      <tr>
        <td style="padding: 3px 0; font-weight: bold; border: none;">${cat}:</td>
        <td style="padding: 3px 0; text-align: right; border: none;">${count} ${count === 1 ? 'producto' : 'productos'}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Catálogo de Productos - Keiks Repostería</title>
          <style>
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #333; margin: 20px; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #46371c; padding-bottom: 10px; margin-bottom: 15px; }
            h1 { font-size: 20px; color: #46371c; margin: 0 0 4px 0; }
            p { font-size: 11px; color: #666; margin: 2px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
            th { background-color: #f4f1ea; border: 1px solid #e5dfd3; color: #46371c; padding: 8px; text-align: left; font-weight: bold; }
            td { border: 1px solid #e5dfd3; padding: 6px 8px; vertical-align: top; }
            tr:nth-child(even) { background-color: #fcfbf9; }
            .summary-box { margin-top: 20px; border: 1px solid #e5dfd3; border-radius: 10px; padding: 12px 15px; max-width: 360px; background-color: #fcfbf9; font-size: 11px; float: right; }
            @media print {
              body { margin: 10px; }
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>CATÁLOGO DE PRODUCTOS CREADOS - KEIKS REPOSTERÍA</h1>
              <p>Generado el: ${new Date().toLocaleDateString('es-CO')} a las ${new Date().toLocaleTimeString('es-CO')}</p>
            </div>
            <div style="text-align: right;">
              <p style="font-size: 12px; font-weight: bold; color: #46371c;">Total Productos: ${products.length}</p>
              <p>Activos: ${activeCount} | Inactivos: ${inactiveCount}</p>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 30px; text-align: center;">#</th>
                <th style="width: 24%;">Producto / Descripción</th>
                <th style="width: 13%;">Categoría</th>
                <th style="width: 10%;">Precio Base</th>
                <th style="width: 22%;">Tamaños y Precios</th>
                <th style="width: 18%;">Sabores y Rellenos</th>
                <th style="width: 10%; text-align: center;">Estado</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <div class="summary-box">
            <h3 style="margin: 0 0 8px 0; color: #46371c; border-bottom: 1px solid #e5dfd3; padding-bottom: 5px; font-size: 12px;">Resumen por Categoría</h3>
            <table style="width: 100%; border-collapse: collapse; margin-top: 0;">
              ${summaryRowsHtml}
              <tr style="border-top: 1.5px solid #46371c; font-weight: bold;">
                <td style="padding: 6px 0 0 0; color: #46371c; border: none;">TOTAL PRODUCTOS:</td>
                <td style="padding: 6px 0 0 0; text-align: right; color: #46371c; border: none;">${products.length}</td>
              </tr>
            </table>
          </div>
          <div style="clear: both;"></div>

          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#46371c]">Productos</h1>
        <div className="flex items-center gap-2.5">
          <Button
            type="button"
            onClick={exportProductsPDF}
            disabled={loading || products.length === 0}
            className="bg-red-600 text-white hover:bg-red-700 rounded-full font-body text-xs sm:text-sm h-10 px-5 disabled:opacity-50"
            data-testid="export-products-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" /> Generar PDF
          </Button>
          <Button onClick={openCreate} className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body text-xs sm:text-sm h-10 px-5" data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-2" /> Agregar
          </Button>
        </div>
      </div>

      <Card className="border border-[#e5dfd3] bg-[#fcfbf9] overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <Table className="min-w-[700px]">
            <TableHeader>
              <TableRow className="bg-[#f4f1ea]">
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Producto</TableHead>
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Categoria</TableHead>
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Precio</TableHead>
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Estado</TableHead>
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map(p => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      <div>
                        <p className="font-body text-sm font-medium text-[#46371c]">{p.name}</p>
                        <p className="font-body text-xs text-[#75664d] line-clamp-1">{p.description}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><Badge className="bg-[#f4f1ea] text-[#46371c] border-0 text-xs">{categoryNames[p.category] || p.category}</Badge></TableCell>
                  <TableCell className="font-body text-sm font-semibold text-[#46371c]">{formatPrice(p.base_price)}</TableCell>
                  <TableCell>
                    <Badge className={p.available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {p.available ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(p)} className="p-2 hover:bg-[#f4f1ea] rounded-lg transition-colors" data-testid={`edit-product-${p.id}`}>
                        <Pencil className="w-4 h-4 text-[#75664d]" />
                      </button>
                      <button onClick={() => handleDelete(p.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-product-${p.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto bg-[#fcfbf9] p-5 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#46371c]">{editing ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#75664d]">Completa los datos del producto</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Nombre</label>
              <input value={form.name} onChange={e => handleChange('name', e.target.value)} required className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="product-name-input" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Descripcion</label>
              <textarea value={form.description} onChange={e => handleChange('description', e.target.value)} rows={2} className="w-full p-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba] resize-none" data-testid="product-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Categoria</label>
                <Select value={form.category} onValueChange={v => handleChange('category', v)}>
                  <SelectTrigger className="rounded-xl border-[#e5dfd3]" data-testid="product-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryNames).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Precio base (COP)</label>
                <input type="number" value={form.base_price} onChange={e => handleChange('base_price', Number(e.target.value))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="product-price-input" />
              </div>
            </div>
            <div>
              <label className="font-body text-sm font-semibold text-[#46371c] mb-2 block">
                Fotos del Producto (Máx. 5)
              </label>
              <div className="grid grid-cols-5 gap-2" data-testid="product-images-grid">
                {[0, 1, 2, 3, 4].map((idx) => {
                  const imgUrl = form.images && form.images[idx];
                  if (imgUrl) {
                    return (
                      <div key={idx} className="relative w-full aspect-square rounded-xl overflow-hidden group border border-[#e5dfd3]">
                        <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(idx)}
                            className="p-1.5 bg-red-600/80 hover:bg-red-600 rounded-full text-white transition-colors"
                            data-testid={`remove-image-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {idx === 0 && (
                          <span className="absolute bottom-1 left-1 bg-[#46371c] text-[#fcfbf9] text-[9px] px-1.5 py-0.5 rounded font-body font-medium">
                            Principal
                          </span>
                        )}
                      </div>
                    );
                  } else {
                    return (
                      <label key={idx} className="relative w-full aspect-square rounded-xl border-2 border-dashed border-[#e5dfd3] hover:border-[#a0d5ba] bg-[#fcfbf9] hover:bg-[#eef6f2] flex flex-col items-center justify-center cursor-pointer transition-colors group">
                        <Plus className="w-5 h-5 text-[#75664d] group-hover:text-[#a0d5ba] transition-colors" />
                        <span className="text-[9px] text-[#75664d] mt-1 font-body group-hover:text-[#a0d5ba] transition-colors">Subir</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, idx)}
                          className="hidden"
                          data-testid={`product-image-upload-${idx}`}
                        />
                      </label>
                    );
                  }
                })}
              </div>
              <p className="text-[11px] text-[#75664d] mt-2 font-body">
                Sube hasta 5 fotos. La primera foto (izquierda) será la imagen principal.
              </p>
            </div>
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                <label className="font-body text-sm font-semibold text-[#46371c]">
                  Tamaños y Precios
                </label>
                <div className="flex flex-wrap gap-2">
                  {form.category === 'tortas-especiales' && (
                    <button
                      type="button"
                      onClick={() => {
                        const defaultSizes = [
                          { name: '12 a 15 porciones', price: form.base_price || 120000, prep_days: 2 },
                          { name: '20 a 25 porciones', price: (form.base_price || 120000) + 50000, prep_days: 3 },
                          { name: '30 a 35 porciones', price: (form.base_price || 120000) + 100000, prep_days: 4 }
                        ];
                        handleChange('sizes', defaultSizes);
                      }}
                      className="text-[10px] bg-[#eef6f2] text-[#347a54] px-2 py-1 rounded-md font-medium hover:bg-[#e2f0e9] font-body"
                      data-testid="suggest-special-sizes-btn"
                    >
                      Usar Tamaños Especiales
                    </button>
                  )}
                  {(form.category === 'refrigeradas' || form.category === 'refrigeradas-premium') && (
                    <button
                      type="button"
                      onClick={() => {
                        const defaultSizes = [
                          { name: '6 a 8 porciones', price: form.base_price || 85000, prep_days: 1 },
                          { name: '12 a 15 porciones', price: (form.base_price || 85000) + 40000, prep_days: 2 },
                          { name: '18 a 22 porciones', price: (form.base_price || 85000) + 80000, prep_days: 3 }
                        ];
                        handleChange('sizes', defaultSizes);
                      }}
                      className="text-[10px] bg-[#eef6f2] text-[#347a54] px-2 py-1 rounded-md font-medium hover:bg-[#e2f0e9] font-body"
                      data-testid="suggest-refrigerated-sizes-btn"
                    >
                      Usar Tamaños Refrigerados
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const newSizes = [...(form.sizes || [])];
                      newSizes.push({ name: '', price: form.base_price || 0, prep_days: form.preparation_days || 2 });
                      handleChange('sizes', newSizes);
                    }}
                    className="text-xs font-semibold text-[#a0d5ba] hover:text-[#82ba9e] flex items-center gap-1 font-body"
                    data-testid="add-size-btn"
                  >
                    <Plus className="w-3.5 h-3.5" /> Agregar
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                {(form.sizes || []).map((size, index) => (
                  <div key={index} className="flex gap-2 items-center" data-testid={`size-row-${index}`}>
                    <input
                      placeholder="Nombre (ej. Mediana)"
                      value={size.name}
                      onChange={e => {
                        const newSizes = [...form.sizes];
                        newSizes[index].name = e.target.value;
                        handleChange('sizes', newSizes);
                      }}
                      required
                      className="flex-1 h-9 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                      data-testid={`size-name-input-${index}`}
                    />
                    <input
                      type="number"
                      placeholder="Precio (COP)"
                      value={size.price}
                      onChange={e => {
                        const newSizes = [...form.sizes];
                        newSizes[index].price = Number(e.target.value);
                        handleChange('sizes', newSizes);
                      }}
                      required
                      className="w-28 h-9 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                      data-testid={`size-price-input-${index}`}
                    />
                    <input
                      type="number"
                      placeholder="Días"
                      value={size.prep_days}
                      onChange={e => {
                        const newSizes = [...form.sizes];
                        newSizes[index].prep_days = Number(e.target.value);
                        handleChange('sizes', newSizes);
                      }}
                      required
                      className="w-16 h-9 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                      data-testid={`size-days-input-${index}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newSizes = [...form.sizes];
                        newSizes.splice(index, 1);
                        handleChange('sizes', newSizes);
                      }}
                      className="p-1.5 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-600 transition-colors"
                      data-testid={`remove-size-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {(form.sizes || []).length === 0 && (
                  <p className="text-xs text-[#75664d] italic font-body">Sin tamaños específicos. Se usará el precio base.</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[#e5dfd3]/50 pt-3">
              {/* Flavors list */}
              <div className="space-y-3">
                <label className="font-body text-sm font-semibold text-[#46371c]">Sabores Permitidos</label>
                <div className="space-y-2 max-h-40 overflow-y-auto pr-1 border border-[#e5dfd3] p-3 rounded-xl bg-white">
                  {globalFlavors.length === 0 ? (
                    <p className="text-xs text-[#75664d] italic font-body">Crea sabores primero en Configuración.</p>
                  ) : (
                    globalFlavors.map((flavor, index) => {
                      const isChecked = !!(form.flavors || []).find(f => f.name === flavor.name);
                      return (
                        <label key={index} className="flex items-center gap-2 font-body text-xs text-[#46371c] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={e => {
                              let newFlavors = [...(form.flavors || [])];
                              if (e.target.checked) {
                                newFlavors.push({ name: flavor.name, price: flavor.price, is_refrigerated: flavor.is_refrigerated });
                              } else {
                                newFlavors = newFlavors.filter(f => f.name !== flavor.name);
                              }
                              handleChange('flavors', newFlavors);
                            }}
                            className="rounded animate-fade-in"
                          />
                          <span>{flavor.name} (+{formatPrice(flavor.price)})</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Fillings list */}
              <div className="space-y-3">
                <label className="font-body text-sm font-semibold text-[#46371c]">Rellenos Permitidos</label>
                <div className="space-y-2 max-h-40 overflow-y-auto pr-1 border border-[#e5dfd3] p-3 rounded-xl bg-white">
                  {globalFillings.length === 0 ? (
                    <p className="text-xs text-[#75664d] italic font-body">Crea rellenos primero en Configuración.</p>
                  ) : (
                    globalFillings.map((filling, index) => {
                      const isChecked = !!(form.fillings || []).find(f => f.name === filling.name);
                      return (
                        <label key={index} className="flex items-center gap-2 font-body text-xs text-[#46371c] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={e => {
                              let newFillings = [...(form.fillings || [])];
                              if (e.target.checked) {
                                newFillings.push({ name: filling.name, price: filling.price });
                              } else {
                                newFillings = newFillings.filter(f => f.name !== filling.name);
                              }
                              handleChange('fillings', newFillings);
                            }}
                            className="rounded animate-fade-in"
                          />
                          <span>{filling.name} (+{formatPrice(filling.price)})</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Dias prep.</label>
                <input type="number" value={form.preparation_days} onChange={e => handleChange('preparation_days', Number(e.target.value))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="product-prep-days-input" />
              </div>
              <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer pb-2">
                <input type="checkbox" checked={form.is_featured} onChange={e => handleChange('is_featured', e.target.checked)} className="rounded" /> Destacado
              </label>
              <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer pb-2">
                <input type="checkbox" checked={form.is_popular} onChange={e => handleChange('is_popular', e.target.checked)} className="rounded" /> Popular
              </label>
              <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer pb-2">
                <input type="checkbox" checked={form.pide_hoy} onChange={e => handleChange('pide_hoy', e.target.checked)} className="rounded" /> Pide Hoy
              </label>
              <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer pb-2">
                <input type="checkbox" checked={form.show_on_website !== false} onChange={e => handleChange('show_on_website', e.target.checked)} className="rounded" /> Ver en Web
              </label>
            </div>
            <div className="flex gap-4 border-t border-[#e5dfd3]/50 pt-3 flex-wrap">
              <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer">
                <input type="checkbox" checked={form.allow_color} onChange={e => handleChange('allow_color', e.target.checked)} className="rounded" /> Habilitar Color
              </label>
              <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer">
                <input type="checkbox" checked={form.allow_flavor} onChange={e => handleChange('allow_flavor', e.target.checked)} className="rounded" /> Habilitar Sabor
              </label>
              <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer">
                <input type="checkbox" checked={form.allow_filling} onChange={e => handleChange('allow_filling', e.target.checked)} className="rounded" /> Habilitar Relleno
              </label>
              <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer">
                <input type="checkbox" checked={form.include_in_cash_closing !== false} onChange={e => handleChange('include_in_cash_closing', e.target.checked)} className="rounded" /> Incluir en Cuadre de Caja Diario
              </label>
            </div>
            <div className="border-t border-[#e5dfd3]/50 pt-3">
              <label className="font-body text-sm font-semibold text-[#46371c] mb-2 block">Métodos de Entrega Disponibles</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(form.delivery_methods || []).includes('pickup')}
                    onChange={e => {
                      const current = form.delivery_methods || [];
                      const next = e.target.checked ? [...current, 'pickup'] : current.filter(m => m !== 'pickup');
                      handleChange('delivery_methods', next);
                    }}
                    className="rounded"
                    data-testid="delivery-method-pickup"
                  />
                  Recoger en Tienda
                </label>
                <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(form.delivery_methods || []).includes('delivery')}
                    onChange={e => {
                      const current = form.delivery_methods || [];
                      const next = e.target.checked ? [...current, 'delivery'] : current.filter(m => m !== 'delivery');
                      handleChange('delivery_methods', next);
                    }}
                    className="rounded"
                    data-testid="delivery-method-delivery"
                  />
                  Envío a Domicilio
                </label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full border-[#e5dfd3] text-[#46371c] font-body">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="save-product-btn">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
